import { Button, notification } from 'antd';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { injectIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { Icon } from 'ui';
import useDeepCompareEffect from 'use-deep-compare-effect';
import moment from 'utils/cops-moment';
import { withRouter } from 'utils/withRouter';
import { errorTypes } from '../../../utils/app-configs';
import HeaderButton from '../../header/components/header-button';
import DuplicationError from './duplication-error';
import MoreDataInfo from './more-data-info';
import MultiLineError from './multiline-error';
import UploadModal from './upload-modal';

const ErrorTitle = styled.p`
  font-weight: bold;
  font-size: 15px;
`;

const StyledInfo = styled.p`
  white-space: pre-wrap;
  margin-top: 10px;
`;

const StyledInfoReference = styled.p`
  margin-top: 10px;
  font-size: 12px;
`;

const StyledList = styled.ul`
  margin-bottom: 10px;
  padding-left: 20px;
`;

const StyledListItem = styled.li`
  margin-top: 5px;
  list-style-type: circle;
`;

const StyledDiv = styled.div`
  margin-top: 5px;
`;

export const UploadButton = styled(Button)`
  text-transform: uppercase;
  border: 2px solid #6f7e95 !important;
  color: ${p => p.primary ? 'white' : '#6f7e95'} !important;
  background-color: ${p => p.primary ? '#6f7e95' : 'white'} !important;
  &:hover {
    border: 2px solid #6f7e95 !important;
    color: ${p => p.primary ? 'white' : '#6f7e95'} !important;
    background-color: ${p => p.primary ? '#6f7e95' : 'white'} !important;
    opacity: 0.7 !important;
  }
  &:focus {
    border: 2px solid #6f7e95 !important;
    color: ${p => p.primary ? 'white' : '#6f7e95'} !important;
    background-color: ${p => p.primary ? '#6f7e95' : 'white'} !important;
  }
  &:disabled {
    opacity: 0.7;
  }
  ${p => p.marginTop && `margin-top: ${p.marginTop}px`};
`;

const CloseIcon = styled(Icon)`
  color: #A3ADBA;
`;

const Overlay = styled.div`
    height: auto;
    min-height: 100%;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    background-color: #6f7e95;
    z-index: 130;
    opacity: 0.5;
`;

const UploadXMLButton = ({
  isLoading,
  uploadFileRequest,
  error,
  intl,
  redirectUri,
  history,
  id,
  userName,
  isCreating,
  createXMLFailed,
  createXMLRequest,
  setSelectedEquipment,
  selectedEquipment,
  customerCenter,
  needsMoreData,
  requestedData,
  deleteDrafts,
  uploadAction,
  serviceCenters,
}) => {
  const [notificationApi, contextHolder] = notification.useNotification();
  const [modalVisible, setModalVisible] = useState(false);
  const [disableBackground, setDisableBackground] = useState(false);
  const dispatch = useDispatch();

  const closeMoreInfoNotification = () => {
    notificationApi.destroy('moreInfoNotification');
    setDisableBackground(false);
  };

  useEffect(() => {
    if (createXMLFailed) {
      notificationApi.destroy('errorNotification');
      closeMoreInfoNotification();
      notificationApi.destroy('poNumberWarning');
      const errorMsg =
        <>
          <ErrorTitle>
            {intl.formatMessage({ id: 'fileUpload.ERROR_TITLE' })}
          </ErrorTitle>
          <p>{intl.formatMessage({ id: 'fileUpload.ERROR_TECHNICAL' })}</p>
          <StyledInfo>
            {intl.formatMessage({ id: 'fileUpload.ERROR_SUPPORT' })}:
          </StyledInfo>
          <StyledInfo>
            {intl.formatMessage({ id: 'fileUpload.NAME' })}{': '}{userName}
          </StyledInfo>
          <p>
            {intl.formatMessage({ id: 'fileUpload.SERVICE_CENTER' })}
            {': '}{JSON.parse(localStorage.getItem('serviceCenter')).value ||
              intl.formatMessage({ id: 'fileUpload.UNKNOWN' })}
          </p>
          <p>
            {intl.formatMessage({ id: 'fileUpload.URL' })}{': '}{window.location.href}
          </p>
          <p>
            {intl.formatMessage({ id: 'fileUpload.TIMESTAMP' })}
            {': '}{moment().format('HH:mm DD/MM/YYYY')}
          </p>
        </>;
      notificationApi.error({
        message: errorMsg,
        style: {
          width: 500,
        },
        duration: 0,
        placement: 'top',
      });
    }
  }, [createXMLFailed]);

  useDeepCompareEffect(() => {
    if (Object.keys(error)?.length || isCreating || selectedEquipment >= 0) {
      let errorMsg =
        <>
          <ErrorTitle>{error?.title}</ErrorTitle>
          <StyledInfo>{error?.message || "Couldn't complete upload!"}</StyledInfo>
          <StyledInfoReference>{error?.reference}</StyledInfoReference>
        </>;

      if (error?.type === errorTypes.multiLine &&
        (
          error?.numberOfDuplicates !== 0 ||
          error?.numberNotFound !== 0
        )
      ) {
        errorMsg = <MultiLineError
          {...error}
          isCreating={isCreating}
          intl={intl}
          createXMLRequest={createXMLRequest}
          customerCenter={
            serviceCenters?.find(s => s.id === customerCenter)?.name ||
            customerCenter
          }
        />;
      }
      if (error?.errors?.length) {
        if (error?.type === errorTypes.duplication) {
          errorMsg = <DuplicationError
            title={error?.title}
            errors={error?.errors}
            reference={error?.reference}
            isCreating={isCreating}
            intl={intl}
            createXMLRequest={createXMLRequest}
            selectedEquipment={selectedEquipment}
            actions={error?.actions}
            setSelectedEquipment={e => setSelectedEquipment(e.target.value)}
          />;
        } else {
          errorMsg =
            <>
              {error?.title && <ErrorTitle>{error?.title}</ErrorTitle>}
              <p>{error?.message || "Couldn't complete upload!"}</p>
              {error?.errors?.length > 1 ?
                <StyledList>
                  {error?.errors?.map(e =>
                    <StyledListItem key={e}>
                      {e}
                    </StyledListItem>,
                  )}
                </StyledList> :
                error?.errors?.map(e =>
                  <StyledDiv key={e}>
                    {e}
                  </StyledDiv>,
                )
              }
              <StyledInfoReference>{error?.reference}</StyledInfoReference>
            </>;
        }
      }
      if (!isCreating) {
        if (
          error?.type === errorTypes.duplication ||
          error?.type === errorTypes.multiLine
        ) {
          notificationApi.warning({
            message: errorMsg,
            placement: 'top',
            key: 'errorNotification',
            style: {
              width: error?.type === errorTypes.multiLine ? 700 : 500,
              marginLeft: error?.type === errorTypes.multiLine ? -50 : 0,
            },
            duration: 0,
          });
        } else {
          closeMoreInfoNotification();
          notificationApi.error({
            message: errorMsg,
            placement: 'top',
            key: 'errorNotification',
            style: {
              width: 500,
            },
            duration: 0,
            btn: <UploadButton
              primary={1}
              onClick={() => notificationApi.destroy('errorNotification')}
            >
              {intl.formatMessage({ id: 'common.OK' })}
            </UploadButton>,
          });
        }
      }
      // Next, close the upload form.
      setModalVisible(false);
    }
  }, [error]);

  useEffect(() => {
    if (needsMoreData) {
      setModalVisible(false);
      notificationApi.destroy('errorNotification');
      notificationApi.info({
        message: requestedData?.title,
        placement: 'top',
        description: <MoreDataInfo
          {...requestedData}
          intl={intl}
          deleteDrafts={deleteDrafts}
          uploadAction={uploadAction}
          placement={'top'}
          closeMoreInfoNotification={closeMoreInfoNotification}
        />,
        style: {
          width: 1443,
        },
        closeIcon: <CloseIcon
          className='fa-duotone fa-xmark'
          size='lg'
          onClick={() => {
            closeMoreInfoNotification();
            dispatch(deleteDrafts({ ids: requestedData.multiLineIds }));
          }}
        />,
        duration: 0,
        key: 'moreInfoNotification',
        className: 'extended-notification',
      });
      setDisableBackground(true);
    }
  }, [JSON.stringify(needsMoreData)]);

  useEffect(() => {
    if (redirectUri) {
      // Next, close the upload form.
      notificationApi.destroy('errorNotification');
      closeMoreInfoNotification();
      history.push(`/${redirectUri}`, { context: id });
    }
  }, [redirectUri]);

  const doFileUpload = ({ file }) => {
    uploadFileRequest({ file });
  };

  return (
    <>
      {disableBackground && <Overlay />}
      {contextHolder}
      <HeaderButton
        icon='upload'
        text={intl.formatMessage({ id: 'common.labels.UPLOAD_XML' })}
        onClick={() => setModalVisible(true)}
        borderLeft={true}
      />
      <UploadModal
        visible={modalVisible}
        isLoading={isLoading}
        doFileUpload={doFileUpload}
        onClose={() => setModalVisible(false)}
        title={intl.formatMessage({
          id: 'header.upload.TITLE',
        })}
        fileType='XML'
      />
    </>
  );
};

UploadXMLButton.propTypes = {
  isLoading: PropTypes.bool,
  uploadFileRequest: PropTypes.func.isRequired,
  error: PropTypes.object,
  intl: PropTypes.object,
  redirectUri: PropTypes.string,
  history: PropTypes.object,
  id: PropTypes.string,
  userName: PropTypes.string,
  isCreating: PropTypes.bool,
  createXMLFailed: PropTypes.bool,
  createXMLRequest: PropTypes.func,
  setSelectedEquipment: PropTypes.func,
  selectedEquipment: PropTypes.number,
  customerCenter: PropTypes.string,
  needsMoreData: PropTypes.bool,
  requestedData: PropTypes.object,
  deleteDrafts: PropTypes.func,
  uploadAction: PropTypes.func,
  serviceCenters: PropTypes.array,
};

export default withRouter(injectIntl(UploadXMLButton));
